// src/pages/ArticlePage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, Phone } from 'lucide-react';
import { BLOG_POSTS } from '@/mock';
import { Header } from '@/components/SiteUI/index'; // Corrected import path
import SEO from '@/components/SEO/SEO';
import ShareButtons from '@/components/ui/share-buttons';
import posts from '@/shared/api/posts';

const ArticlePage = () => {
    const { slug } = useParams();
    // const article = BLOG_POSTS.find(post => post.id === id);
    const [article, setArticle] = useState(null);

    const PHONE_NUMBER = "+375 (44) 568-69-72";
    const PHONE_TEL = "tel:+375445686972";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        const fetchArticle = async () => {
            try {
                const res = await posts.getPost(slug);
                setArticle(res.post);
            } catch (err) {
                console.error(err.message);
            }
        }

        fetchArticle();
    }, [slug]);

    const formatDate = (date) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(date).toLocaleDateString('ru-RU', options);
    }

    if (!article) {
        return (
            <>
                <SEO 
                    title="Статья не найдена"
                    description="Запрошенная статья не была найдена"
                />
                <div className="min-h-screen pt-20 flex items-center justify-center">
                    <Header />
                    <div className="text-center">
                        <h1 className="text-2xl font-serif text-olive-950 mb-4">Статья не найдена</h1>
                        <Button asChild>
                            <Link to="/">Вернуться на главную</Link>
                        </Button>
                    </div>
                </div>
            </>
        );
    }


    return (
        <>
            <SEO 
                title={article.title}
                description={article.content ? article.content.replace(/<[^>]*>/g, '').slice(0, 160) : `Статья по психологии: ${article.title}`}
                keywords="психология, психологическая помощь, консультация психолога, психотерапия, саморазвитие, психолог минск"
                url={`/article/${article.slug}`}
                type="article"
                article={{
                    publishedTime: article.createdAt,
                    modifiedTime: article.updatedAt || article.createdAt,
                    author: "Психолог в Минске",
                    section: "Психология",
                    tags: ["психология", "консультация", "помощь психолога"]
                }}
                image={article.image}
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": article.title,
                    "description": article.content ? article.content.replace(/<[^>]*>/g, '').slice(0, 160) : `Статья по психологии: ${article.title}`,
                    "image": article.image || "https://cope.by/images/og-article.jpg",
                    "datePublished": article.createdAt,
                    "dateModified": article.updatedAt || article.createdAt,
                    "author": {
                        "@type": "Person",
                        "name": "Психолог в Минске"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Психологическая консультация в Минске",
                        "url": "https://cope.by"
                    },
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `https://cope.by/article/${article.slug}`
                    }
                }}
            />
            <div className="min-h-screen bg-white text-olive-900">
            <Header />
            <main className="pt-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <Button
                        variant="ghost"
                        className="text-olive-700"
                        onClick={() => window.history.back()}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Назад
                    </Button>

                    <article>
                        <header className="text-center mb-12">
                            <h1 className="font-serif text-3xl text-olive-950 mb-6 leading-tight">
                                {article.title}
                            </h1>

                            <div className="flex items-center justify-between gap-6 text-olive-900/70 mb-8">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span>{formatDate(article.createdAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        <span>{article.timeToRead} мин чтения</span>
                                    </div>
                                </div>
                                <ShareButtons 
                                    title={article.title}
                                    description={article.content ? article.content.replace(/<[^>]*>/g, '').slice(0, 160) : `Статья по психологии: ${article.title}`}
                                    url={`/article/${article.slug}`}
                                    showText={false}
                                />
                            </div>

                            {article.image && (
                                <div className="mb-8 rounded-2xl overflow-hidden">
                                    <img
                                        src={article.image}
                                        alt={article.title}
                                        className="w-full h-64 md:h-80 object-cover"
                                    />
                                </div>
                            )}
                        </header>

                        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-olive-100">
                            <div className="prose prose-lg max-w-none">
                                <p className="text-sm text-olive-900/80 leading-relaxed mb-6 font-medium" dangerouslySetInnerHTML={{ __html: article.content }}></p>
                            </div>

                            <div className="bg-olive-50  mt-12 p-6 bg-gradient-to-r from-olive-50 to-olive-100 rounded-xl border border-olive-200">
                                <h3 className="text-sm font-semibold text-olive-900 mb-2">
                                    Нужна индивидуальная консультация?
                                </h3>
                                <p className="text-sm text-olive-900/80 leading-relaxed mb-6 font-medium">
                                    Если тема статьи оказалась для вас актуальной, и вы хотите
                                    проработать ее глубже, запишитесь на индивидуальную консультацию.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Button asChild>
                                        <a href="/#booking">
                                            Записаться на консультацию
                                        </a>
                                    </Button>
                                    <Button asChild variant="outline" className="border-olive-200">
                                        <a href={PHONE_TEL}>
                                            <Phone className="w-4 h-4 mr-2" />
                                            {PHONE_NUMBER}
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12">
                            <ShareButtons 
                                title={article.title}
                                description={article.content ? article.content.replace(/<[^>]*>/g, '').slice(0, 160) : `Статья по психологии: ${article.title}`}
                                url={`/article/${article.slug}`}
                                className="justify-center"
                            />
                        </div>
                    </article>
                </div>
            </main>
            </div>
        </>
    );
};

export default ArticlePage;